import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Input, Row, Col, Space, Grid, message, Select, List, Pagination, Table } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined, ToolOutlined, CheckCircleOutlined, StopOutlined, PercentageOutlined, UploadOutlined } from '@ant-design/icons';
import { getAssets, deleteAsset } from '@/api/assets';
import type { Asset, AssetCategory, AssetStatus } from '@/types/resources/asset';
import { AssetModal } from '@/components/features/resources/assets/AssetModal';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { AssetsBulkUploadModal } from '@/components/features/resources/assets/AssetsBulkUploadModal';
import assetsListJson from '@/utils/assetsList.json';
import { useSimpleCRUDTour } from '@/hooks/useSimpleCRUDTour';
import { TourButton } from '@/components/common/TourButton';

const PAGE_SIZE = 10;

const Assets: React.FC = () => {
  const { t } = useTranslation(['assets', 'common']);
  const { firestoreUser, workspaceStats } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const screens = Grid.useBreakpoint();
  const { startCRUDTour, isActive } = useSimpleCRUDTour();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | ''>('');
  const [selectedStatus, setSelectedStatus] = useState<AssetStatus | ''>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0
  });
  const [currentSlide, setCurrentSlide] = useState(0);
  const sliderRef = React.useRef<HTMLDivElement>(null);

  // Queries
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['assets', pagination.current, pagination.pageSize, searchTerm, selectedCategory, selectedStatus],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getAssets({
        page: pagination.current,
        pageSize: pagination.pageSize,
        searchTerm: searchTerm,
        category: selectedCategory || undefined,
        status: selectedStatus || undefined,
        firestoreUser
      });
    },
    enabled: !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
  
  useEffect(() => {
    if (data?.total) {
      setPagination(prev => ({ ...prev, total: data.total || 0 }));
    }
  }, [data?.total]);

  // Calculate asset stats from workspace stats
  const assetStats = React.useMemo(() => {
    // Get stats from workspace stats (which come from mock data)
    const totalAssets = workspaceStats?.totalAssets || 0;
    const activeAssets = Math.round(totalAssets * 0.8); // 80% active (good + new statuses)
    const activeRate = totalAssets > 0 ? Math.round((activeAssets / totalAssets) * 100) : 0;
    
    const result = [
      {
        title: t('assets:stats.totalAssets'),
        value: totalAssets,
        icon: <ToolOutlined style={{ color: '#1890ff' }} />
      },
      {
        title: t('assets:stats.activeAssets'),
        value: activeAssets,
        icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />
      },
      {
        title: t('assets:stats.maintenanceAssets'),
        value: Math.round(totalAssets * 0.1), // 10% maintenance
        icon: <ToolOutlined style={{ color: '#faad14' }} />
      },
      {
        title: t('assets:stats.retiredAssets'),
        value: Math.round(totalAssets * 0.05), // 5% retired
        icon: <StopOutlined style={{ color: '#ff4d4f' }} />
      },
      {
        title: t('assets:stats.activeRate'),
        value: `${activeRate}%`,
        icon: <PercentageOutlined style={{ color: '#eb2f96' }} />
      }
    ];
    
    return result;
  }, [workspaceStats, t]);

  // Stats slider functions
  const scrollToSlide = (index: number) => {
    if (sliderRef.current) {
      const cardWidth = 140;
      sliderRef.current.scrollTo({
        left: index * cardWidth,
        behavior: 'smooth'
      });
      setCurrentSlide(index);
    }
  };

  const handleScroll = () => {
    if (sliderRef.current) {
      const scrollLeft = sliderRef.current.scrollLeft;
      const cardWidth = 140;
      const newSlide = Math.round(scrollLeft / cardWidth);
      setCurrentSlide(newSlide);
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (slider) {
      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteAsset(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('assets:messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      setShowDeleteModal(false);
    },
    onError: (error) => {
      message.error(t('assets:messages.deleteError'));
      console.error('Error deleting asset:', error);
    }
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingAsset(null);
    setIsModalOpen(true);
  }, []);

  const handleBulkUpload = useCallback(() => {
    setOpenBulkUploadDialog(true);
  }, []);

  const handleBulkUploadSuccess = useCallback(() => {
    setOpenBulkUploadDialog(false);
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  }, [queryClient]);

  const handleBulkUploadCancel = useCallback(() => {
    setOpenBulkUploadDialog(false);
  }, []);

  const handleEdit = useCallback((asset: Asset) => {
    setEditingAsset(asset);
    setIsModalOpen(true);
  }, []);

  const handleDelete = useCallback((asset: Asset) => {
    setAssetToDelete(asset);
    setShowDeleteModal(true);
  }, []);

  const handleViewDetails = useCallback((asset: Asset) => {
    navigate(`/resources/assets/${asset.id}`, { state: { asset } });
  }, [navigate]);

  const handleModalSuccess = useCallback(() => {
    setIsModalOpen(false);
    setEditingAsset(null);
    queryClient.invalidateQueries({ queryKey: ['assets'] });
  }, [queryClient]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingAsset(null);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSearchTerm(value);
  }, []);

  const handleTableChange = useCallback((newPagination: any) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: newPagination.total
    });
  }, []);

  const handleCategoryChange = (value: AssetCategory | '') => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSelectedCategory(value);
  };

  const handleStatusChange = (value: AssetStatus | '') => {
    setPagination(prev => ({ ...prev, current: 1 }));
    setSelectedStatus(value);
  };

  // Tour handler
  const handleStartTour = useCallback(() => {
    const hasRecords = (data?.assets?.length || 0) > 0;
    const recordCount = data?.total || 0;
    const firstRecordId = data?.assets?.[0]?.id;

    startCRUDTour({
      pageName: 'Assets Management',
      recordType: 'asset',
      hasRecords,
      recordCount,
      firstRecordId,
      onAddRecord: handleAdd,
      onViewRecord: (id: string) => {
        const asset = data?.assets?.find(a => a.id === id);
        if (asset) handleViewDetails(asset);
      },
      onEditRecord: (id: string) => {
        const asset = data?.assets?.find(a => a.id === id);
        if (asset) handleEdit(asset);
      },
      onDeleteRecord: (id: string) => {
        const asset = data?.assets?.find(a => a.id === id);
        if (asset) handleDelete(asset);
      }
    });
  }, [startCRUDTour, data, handleAdd, handleViewDetails, handleEdit, handleDelete]);

  // Memoized values
  const categoryOptions = useMemo(() => {
    return [
      { label: t('common:all'), value: '' },
      ...assetsListJson.map(cat => ({
        label: t(`assets:categories.${cat.slug}`, cat.category),
        value: cat.slug as AssetCategory
      }))
    ];
  }, [t]);

  const statusOptions = useMemo(() => {
    return [
      { label: t('common:all'), value: '' },
      { label: t('assets:status.new'), value: 'new' },
      { label: t('assets:status.good'), value: 'good' },
      { label: t('assets:status.fair'), value: 'fair' },
      { label: t('assets:status.poor'), value: 'poor' },
      { label: t('assets:status.damaged'), value: 'damaged' },
      { label: t('assets:status.active'), value: 'active' },
      { label: t('assets:status.maintenance'), value: 'maintenance' },
      { label: t('assets:status.retired'), value: 'retired' },
      { label: t('assets:status.lost'), value: 'lost' }
    ];
  }, [t]);

  const renderAssetCard = (asset: Asset) => (
    <Card
      key={asset.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(asset)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(asset)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(asset)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={asset.name}
        description={
          <Space direction="vertical" size="small">
            <div>{t('assets:fields.category')}: {t(`assets:categories.${asset.category}`, asset.category)}</div>
            <div>{t('assets:fields.status')}: {t(`assets:status.${asset.status}`, asset.status)}</div>
            <div>{t('assets:fields.location')}: {asset.location || '-'}</div>
            <div>{t('assets:fields.block')}: {asset.block?.name || '-'}</div>
            <div>{t('assets:fields.amenity')}: {asset.amenity?.name || '-'}</div>
            <div>{t('assets:fields.purchasePrice')}: {
              asset.purchasePrice ? (
                typeof asset.purchasePrice === 'number' ? 
                  new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR'
                  }).format(asset.purchasePrice) :
                  new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: asset.purchasePrice.currency || 'INR'
                  }).format(asset.purchasePrice.amount)
              ) : '-'
            }</div>
          </Space>
        }
      />
    </Card>
  );

  const columns = [
    {
      title: t('assets:fields.name'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Asset) => (
        <Button type="link" onClick={() => handleViewDetails(record)}>
          {name}
        </Button>
      )
    },
    {
      title: t('assets:fields.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: AssetCategory) => (
        <span>{t(`assets:categories.${category}`, category)}</span>
      )
    },
    {
      title: t('assets:fields.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: AssetStatus) => (
        <span>{t(`assets:status.${status}`, status)}</span>
      )
    },
    {
      title: t('assets:fields.location'),
      dataIndex: 'location',
      key: 'location',
      render: (location: string) => location || '-'
    },
    {
      title: t('assets:fields.block'),
      dataIndex: ['block', 'name'],
      key: 'block',
      render: (blockName: string) => blockName || '-'
    },
    {
      title: t('assets:fields.amenity'),
      dataIndex: ['amenity', 'name'],
      key: 'amenity',
      render: (amenityName: string) => amenityName || '-'
    },
    {
      title: t('assets:fields.purchasePrice'),
      dataIndex: 'purchasePrice',
      key: 'purchasePrice',
      render: (price: any) => {
        if (!price) return '-';
        if (typeof price === 'number') {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
          }).format(price);
        }
        if (price.amount) {
          return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: price.currency || 'INR'
          }).format(price.amount);
        }
        return '-';
      }
    },
    {
      title: t('common:actions.title'),
      key: 'actions',
      render: (_: any, record: Asset) => (
        <Space className="record-actions">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            {t('common:actions.view')}
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            {t('common:actions.edit')}
          </Button>
          <Button
            type="link"
            icon={<DeleteOutlined />}
            danger
            onClick={() => handleDelete(record)}
          >
            {t('common:actions.delete')}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Enhanced Stats Section */}
      <div style={{ marginBottom: 24 }}>
        <div
          ref={sliderRef}
          style={{
            display: 'flex',
            overflowX: screens.xs ? 'auto' : 'visible',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            gap: screens.xs ? '12px' : '8px',
            padding: '8px 0',
            flexWrap: screens.xs ? 'nowrap' : 'wrap',
            justifyContent: screens.xs ? 'flex-start' : 'center'
          }}
        >
          {assetStats.map((stat, idx) => (
            <Card
              key={idx}
              style={{
                minWidth: 120,
                maxWidth: 150,
                width: '100%',
                flex: screens.xs ? '0 0 auto' : '1 1 0',
                cursor: 'pointer',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                border: '1px solid #f0f0f0',
                margin: screens.xs ? 0 : '0 4px'
              }}
              onClick={() => scrollToSlide(idx)}
              hoverable
              styles={{
                body: {
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }
              }}
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '8px',
                marginBottom: '4px'
              }}>
                <div style={{ fontSize: '20px' }}>
                  {stat.icon}
                </div>
                <div style={{ 
                  fontSize: '24px', 
                  fontWeight: '700',
                  color: '#262626'
                }}>
                  {stat.value}
                </div>
              </div>
              <div style={{ 
                fontSize: '14px', 
                color: '#262626',
                textAlign: 'center',
                lineHeight: '1.2',
                fontWeight: '600'
              }}>
                {stat.title}
              </div>
            </Card>
          ))}
        </div>
        
        {/* Progress Indicators for Mobile */}
        {screens.xs && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            gap: '6px', 
            marginTop: '12px' 
          }}>
            {assetStats.map((_, idx) => (
              <div
                key={idx}
                onClick={() => scrollToSlide(idx)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentSlide === idx ? '#1890ff' : '#d9d9d9',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tour Button */}
      <div style={{ marginBottom: 16, textAlign: 'center' }}>
        <TourButton onClick={handleStartTour} disabled={isActive} size="large" />
      </div>

      {screens.xs ? (
        <>
          <List
            dataSource={data?.assets}
            renderItem={renderAssetCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            loading={isLoading || isFetching}
            pagination={false}
            header={
              <>
                <Row gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} style={{ marginBottom: 16 }} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Input.Search
                      id="assets-search-input"
                      placeholder={t('common:searchPlaceholder')}
                      onSearch={handleSearch}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? 8 : 0 }}>
                    <Button 
                      id="assets-add-button"
                      type="primary" 
                      onClick={handleAdd} 
                      icon={<PlusOutlined />} 
                      block={screens.xs}
                    >
                      {t('assets:actions.add')}
                    </Button>
                  </Col>
                </Row>
                <div style={{ 
                  marginBottom: 16,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <Select
                    placeholder={t('assets:filters.category')}
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={categoryOptions}
                  />
                  <Select
                    placeholder={t('assets:filters.status')}
                    value={selectedStatus}
                    onChange={handleStatusChange}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={statusOptions}
                  />
                </div>
              </>
            }
          />
          <div className="pagination-section" style={{ 
            marginTop: 16, 
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <Pagination
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={pagination.total}
              onChange={(page, pageSize) => handleTableChange({ current: page, pageSize, total: pagination.total })}
              showSizeChanger={!screens.xs}
              pageSizeOptions={[10, 25, 50, 100]}
              showQuickJumper={!screens.xs}
              showTotal={!screens.xs ? (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                }) : undefined
              }
              size={screens.xs ? 'small' : 'default'}
              simple={screens.xs}
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            />
          </div>
        </>
      ) : (
        <>
          <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
            <Col>
              <Input.Search
                id="assets-search-input"
                className="search-input"
                placeholder={t('common:searchPlaceholder')}
                onSearch={handleSearch}
                allowClear
                style={{ width: 300 }}
              />
            </Col>
            <Col>
              <Space>
                <Select
                  placeholder={t('assets:filters.category')}
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  allowClear
                  style={{ width: 150 }}
                  options={categoryOptions}
                />
                <Select
                  placeholder={t('assets:filters.status')}
                  value={selectedStatus}
                  onChange={handleStatusChange}
                  allowClear
                  style={{ width: 150 }}
                  options={statusOptions}
                />
                <Button onClick={handleBulkUpload} icon={<UploadOutlined />}>
                  {t('common:actions.bulkUpload')}
                </Button>
                <Button 
                  id="assets-add-button"
                  className="add-record-button"
                  type="primary" 
                  onClick={handleAdd} 
                  icon={<PlusOutlined />}
                >
                  {t('assets:actions.add')}
                </Button>
              </Space>
            </Col>
          </Row>
          
          <Card className="data-table-card">
            <Table
              className="data-table"
              dataSource={data?.assets}
              columns={columns}
              rowKey="id"
              loading={isLoading || isFetching}
              pagination={pagination}
              onChange={handleTableChange}
              scroll={{ x: 'max-content' }}
            />
          </Card>
        </>
      )}

      {isModalOpen && (
        <AssetModal
          open={isModalOpen}
          asset={editingAsset}
          onSuccess={handleModalSuccess}
          onCancel={handleModalCancel}
          category={editingAsset?.category || (selectedCategory as AssetCategory) || 'structural'}
        />
      )}

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          if (assetToDelete) {
            deleteMutation.mutate(assetToDelete.id);
          }
        }}
        title={t('assets:messages.deleteConfirm')}
      />

      <AssetsBulkUploadModal
        visible={openBulkUploadDialog}
        onCancel={handleBulkUploadCancel}
        onSuccess={handleBulkUploadSuccess}
      />
    </div>
  );
};

export default Assets; 